/**
 * Guillotine 2D bin-packing — solver multi-heurístico mejorado.
 *
 * **Port VERBATIM** del antiguo `static/cutlist/optimizer.js` (el algoritmo no se
 * reimplementa): se conserva la misma lógica, los mismos números y el mismo
 * comportamiento; lo único que se añade son las anotaciones de tipo de TypeScript.
 * El cálculo corre 100% en el navegador (el servidor solo persiste el resultado),
 * igual que en el SSR.
 *
 * Cada corte va de lado a lado dentro de su sub-rectángulo → siempre factible en
 * cizalla. Prioridad: cortes horizontales en los splits primarios.
 *
 * Mejoras sobre v1:
 *  · Split 'best': elige H/V en cada placement según qué split deja mejor espacio útil
 *  · Split 'mwf' (Max Waste Free): maximiza el área del rect libre más grande
 *  · Más estrategias de ordenamiento (15)
 *  · Hasta 600 passes aleatorios y mejora por intercambio de pares (swap) en el
 *    esfuerzo máximo
 *
 * **Esfuerzo adaptativo (feature #75):** el usuario ya no elige un "nivel" de
 * optimización. `optimize()` decide internamente cuánto esfuerzo invertir según el
 * número total de instancias de piezas a colocar (ver `chooseEffortLevel`): pocas
 * piezas → esfuerzo máximo; muchas → esfuerzo acotado para no congelar el navegador.
 */
import type {
  CutConfig,
  CutSegment,
  OptimizeResult,
  Part,
  Placement,
  Sheet,
  Stock,
  WasteRect,
} from "./types";

/** Rectángulo libre usado por el packer. */
interface FreeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Tolerancia de límites (cm). Absorbe el ruido de coma flotante al comparar
 * coordenadas contra el borde del tablero, igual que el assert de los tests.
 */
export const BOARD_EPS = 1e-6;

/**
 * Comprueba que una pieza colocada cabe dentro del tablero `W × H`.
 *
 * Defensa en profundidad del bug "la pieza sobresale del borde": el packing se
 * apoya en que cada sub-rectángulo libre queda dentro del tablero, pero un dato
 * de entrada degenerado (o un cambio futuro del algoritmo) podría romper esa
 * invariante. Esta guarda la hace explícita en el punto de colocación.
 */
function fitsInBoard(
  x: number,
  y: number,
  w: number,
  h: number,
  W: number,
  H: number,
): boolean {
  return (
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    Number.isFinite(w) &&
    Number.isFinite(h) &&
    w > 0 &&
    h > 0 &&
    x >= -BOARD_EPS &&
    y >= -BOARD_EPS &&
    x + w <= W + BOARD_EPS &&
    y + h <= H + BOARD_EPS
  );
}

/**
 * Añade un sub-rectángulo libre solo si es útil y queda dentro del tablero.
 *
 * Recorta el rect a los límites `[0, W] × [0, H]` (el kerf nunca debe empujar un
 * rect libre fuera del borde) y lo descarta si tras el recorte su ancho/alto es
 * <= EPS. Así ninguna pieza posterior puede colocarse en un rect "fantasma" que
 * caiga, total o parcialmente, fuera de la superficie de la lámina.
 */
function pushFreeRect(
  freeRects: FreeRect[],
  x: number,
  y: number,
  width: number,
  height: number,
  W: number,
  H: number,
): void {
  const x0 = Math.max(0, x);
  const y0 = Math.max(0, y);
  const x1 = Math.min(W, x + width);
  const y1 = Math.min(H, y + height);
  const w = x1 - x0;
  const h = y1 - y0;
  if (w > 0.01 && h > 0.01) {
    freeRects.push({ x: x0, y: y0, width: w, height: h });
  }
}

/**
 * Sanea una lista de placements contra el tablero: recorta los que sobresalen y
 * descarta los que quedarían fuera o sin área. Red de seguridad final para que
 * el render (`SheetSvg`) nunca reciba una pieza fuera de la superficie, incluso
 * si el `result` proviene de un snapshot del historial guardado por una versión
 * anterior (se recarga sin re-validar en `CutlistScreen`).
 */
export function clampPlacementsToBoard(
  placements: Placement[],
  W: number,
  H: number,
): Placement[] {
  const out: Placement[] = [];
  for (const p of placements) {
    const x0 = Math.max(0, p.x);
    const y0 = Math.max(0, p.y);
    const x1 = Math.min(W, p.x + p.placedWidth);
    const y1 = Math.min(H, p.y + p.placedHeight);
    const w = x1 - x0;
    const h = y1 - y0;
    if (!(w > BOARD_EPS) || !(h > BOARD_EPS)) continue;
    out.push({ ...p, x: x0, y: y0, placedWidth: w, placedHeight: h });
  }
  return out;
}

/** Función heurística de selección de espacio libre. */
type Heuristic = (
  fw: number,
  fh: number,
  pw: number,
  ph: number,
  fx: number,
  fy: number,
) => number;

/** Regla de split del corte primario. */
type SplitRule = "horiz" | "vert" | "sas" | "las" | "best" | "mwf";

/** Resultado interno de un packing (una o varias hojas + piezas sin colocar). */
interface PackingResult {
  sheets: Sheet[];
  unplaced: Part[];
}

/**
 * Pieza expandida por cantidad: una `Part` con un `instanceId` que la identifica
 * dentro de su grupo (como en el `optimizer.js` original). El campo extra no afecta
 * al packing (solo se arrastra en el snapshot); se modela como una extensión de
 * `Part` para no recurrir a un `as` opaco.
 */
type ExpandedPart = Part & { instanceId: string };

// ─── Heurísticas de selección de espacio libre ────────────────────────────────

const BSSF: Heuristic = (fw, fh, pw, ph) =>
  pw > fw || ph > fh ? Infinity : Math.min(fw - pw, fh - ph);
const BLSF: Heuristic = (fw, fh, pw, ph) =>
  pw > fw || ph > fh ? Infinity : Math.max(fw - pw, fh - ph);
const BAF: Heuristic = (fw, fh, pw, ph) =>
  pw > fw || ph > fh ? Infinity : fw * fh - pw * ph;
const BL: Heuristic = (fw, fh, pw, ph, fx, fy) =>
  pw > fw || ph > fh ? Infinity : fy * 1e9 + fx;
const BR: Heuristic = (fw, fh, pw, ph, fx, fy) =>
  pw > fw || ph > fh ? Infinity : fx * 1e9 + fy;

// ─── Split adaptativo ('best') ────────────────────────────────────────────────
// Evalúa cuál split (H o V) deja mayor área aprovechable para las piezas restantes.
// Desempate: prefiere horizontal (mejor para cizalla).

function chooseBestSplit(
  r: FreeRect,
  pw: number,
  ph: number,
  cut: number,
  remaining: Part[],
): boolean {
  const rightW = r.width - pw - cut;
  const bottomH = r.height - ph - cut;

  const useful = (rw: number, rh: number): number => {
    if (rw < 0.01 || rh < 0.01) return 0;
    for (const p of remaining) {
      if (
        (p.width <= rw && p.height <= rh) ||
        (p.canRotate && p.height <= rw && p.width <= rh)
      )
        return rw * rh;
    }
    return 0;
  };

  // Horiz: sub-rect inferior (r.width × bottomH) + sub-rect derecho (rightW × ph)
  const horizScore = useful(r.width, bottomH) + useful(rightW, ph);
  // Vert:  sub-rect derecho (rightW × r.height) + sub-rect inferior (pw × bottomH)
  const vertScore = useful(rightW, r.height) + useful(pw, bottomH);

  return horizScore >= vertScore; // true → H, desempate a favor de H
}

// Split MWF: maximiza el rect libre más grande resultante
function chooseMWFSplit(r: FreeRect, pw: number, ph: number, cut: number): boolean {
  const rightW = r.width - pw - cut;
  const bottomH = r.height - ph - cut;
  const horizBest = Math.max(r.width * bottomH, rightW * ph);
  const vertBest = Math.max(rightW * r.height, pw * bottomH);
  return horizBest >= vertBest; // true → H
}

// ─── Packer guillotine ────────────────────────────────────────────────────────
//
// splitRule: 'horiz' | 'vert' | 'sas' | 'las' | 'best' | 'mwf'
//
// Corte primario: cruza todo el ancho (H) o alto (V) del rect padre → factible en cizalla.
// Corte secundario: solo dentro de la franja ya separada.

function guillotinePack(
  parts: Part[],
  W: number,
  H: number,
  cut: number,
  splitRule: SplitRule,
  hFn: Heuristic,
): {
  placements: Placement[];
  unplaced: Part[];
  cutSegments: CutSegment[];
  wasteRects: WasteRect[];
} {
  const freeRects: FreeRect[] = [{ x: 0, y: 0, width: W, height: H }];
  const placements: Placement[] = [];
  const unplaced: Part[] = [];
  const cutSegments: CutSegment[] = [];

  for (let pi = 0; pi < parts.length; pi++) {
    const part = parts[pi];
    let best = Infinity,
      bestIdx = -1,
      bestRot = false;

    for (let i = 0; i < freeRects.length; i++) {
      const r = freeRects[i];
      const s = hFn(r.width, r.height, part.width, part.height, r.x, r.y);
      if (s < best) {
        best = s;
        bestIdx = i;
        bestRot = false;
      }
      if (part.canRotate && part.width !== part.height) {
        const sr = hFn(r.width, r.height, part.height, part.width, r.x, r.y);
        if (sr < best) {
          best = sr;
          bestIdx = i;
          bestRot = true;
        }
      }
    }

    if (bestIdx < 0 || best === Infinity) {
      unplaced.push(part);
      continue;
    }

    const r = freeRects[bestIdx];
    const pw = bestRot ? part.height : part.width;
    const ph = bestRot ? part.width : part.height;
    const px = r.x,
      py = r.y;

    // Guarda dura: la pieza (ya con ancho/alto intercambiados si está rotada) debe
    // caber dentro del tablero. La heurística ya garantiza `pw<=r.width` y
    // `ph<=r.height` con datos válidos; este chequeo blinda el borde ante datos
    // degenerados o cambios futuros, evitando que un placement se salga de la lámina.
    if (!fitsInBoard(px, py, pw, ph, W, H)) {
      unplaced.push(part);
      continue;
    }

    placements.push({
      ...part,
      x: px,
      y: py,
      placedWidth: pw,
      placedHeight: ph,
      rotated: bestRot,
    });
    freeRects.splice(bestIdx, 1);

    const rightW = r.width - pw - cut;
    const bottomH = r.height - ph - cut;
    const remaining = parts.slice(pi + 1);

    // Elegir dirección del split primario
    let doHoriz: boolean;
    if (splitRule === "horiz") doHoriz = true;
    else if (splitRule === "vert") doHoriz = false;
    else if (splitRule === "sas") doHoriz = rightW <= bottomH;
    else if (splitRule === "las") doHoriz = rightW > bottomH;
    else if (splitRule === "best") doHoriz = chooseBestSplit(r, pw, ph, cut, remaining);
    else doHoriz = chooseMWFSplit(r, pw, ph, cut); // 'mwf'

    if (doHoriz) {
      // ── Corte primario HORIZONTAL (cizalla primer pasada) ─────────────────
      if (bottomH > 0.01 && r.width > 0.01) {
        const cy = py + ph + cut / 2;
        cutSegments.push({ x1: r.x, y1: cy, x2: r.x + r.width, y2: cy, type: "h" });
        pushFreeRect(freeRects, r.x, py + ph + cut, r.width, bottomH, W, H);
      }
      if (rightW > 0.01 && ph > 0.01) {
        const cx = px + pw + cut / 2;
        cutSegments.push({ x1: cx, y1: r.y, x2: cx, y2: r.y + ph, type: "v" });
        pushFreeRect(freeRects, px + pw + cut, r.y, rightW, ph, W, H);
      }
    } else {
      // ── Corte primario VERTICAL ───────────────────────────────────────────
      if (rightW > 0.01 && r.height > 0.01) {
        const cx = px + pw + cut / 2;
        cutSegments.push({ x1: cx, y1: r.y, x2: cx, y2: r.y + r.height, type: "v" });
        pushFreeRect(freeRects, px + pw + cut, r.y, rightW, r.height, W, H);
      }
      if (pw > 0.01 && bottomH > 0.01) {
        const cy = py + ph + cut / 2;
        cutSegments.push({ x1: r.x, y1: cy, x2: r.x + pw, y2: cy, type: "h" });
        pushFreeRect(freeRects, r.x, py + ph + cut, pw, bottomH, W, H);
      }
    }
  }

  return { placements, unplaced, cutSegments, wasteRects: freeRects };
}

// ─── Multi-hoja ───────────────────────────────────────────────────────────────

function runPacking(
  sortedParts: Part[],
  stock: Stock,
  cut: number,
  splitRule: SplitRule,
  hFn: Heuristic,
): PackingResult {
  const sheets: Sheet[] = [];
  let rem: Part[] = [...sortedParts];

  while (rem.length > 0) {
    const { placements, unplaced, cutSegments, wasteRects } = guillotinePack(
      rem,
      stock.width,
      stock.height,
      cut,
      splitRule,
      hFn,
    );
    if (placements.length === 0) break;

    const usedArea = placements.reduce((s, p) => s + p.placedWidth * p.placedHeight, 0);
    const totalArea = stock.width * stock.height;
    sheets.push({
      id: sheets.length,
      stock,
      placements,
      cutSegments,
      wasteRects,
      usedArea,
      wasteArea: totalArea - usedArea,
      totalArea,
      wastePercent: Math.round((1 - usedArea / totalArea) * 100),
    });
    rem = unplaced;
  }
  return { sheets, unplaced: rem };
}

function packingScore({ sheets, unplaced }: PackingResult): number {
  const n = sheets.length;
  // Hojas anteriores pesan el doble → llena la hoja 1 primero
  const weightedWaste = sheets.reduce((s, sh, i) => {
    const weight = Math.pow(2, n - 1 - i);
    return s + sh.wasteArea * weight;
  }, 0);
  // Penaliza rotaciones: prefiere piezas en posición normal cuando caben así.
  // 100 cm² por pieza rotada — pequeño pero suficiente para desempatar.
  const rotationPenalty = sheets.reduce(
    (s, sh) => s + sh.placements.filter((p) => p.rotated).length * 100,
    0,
  );
  return n * 1e12 + unplaced.length * 1e10 + weightedWaste + rotationPenalty;
}

// ─── Shuffle determinista (LCG) ──────────────────────────────────────────────
function shuffle<T>(arr: T[], seed: number): T[] {
  const a = [...arr];
  let s = seed >>> 0;
  for (let i = a.length - 1; i > 0; i--) {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    const j = s % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Mejora por intercambio de pares ─────────────────────────────────────────
// Toma la mejor ordenación encontrada e intenta intercambiar pares de piezas.
// Cada swap genera N combinaciones de split × heurística.

function pairwiseImprove(
  bestParts: Part[],
  stock: Stock,
  cut: number,
  splitRules: SplitRule[],
  heuristics: Heuristic[],
  curBest: PackingResult,
  curScore: number,
): { best: PackingResult; score: number } {
  let improved = true;
  let order = [...bestParts];

  while (improved) {
    improved = false;
    for (let i = 0; i < order.length - 1 && !improved; i++) {
      for (let j = i + 1; j < order.length && !improved; j++) {
        const swapped = [...order];
        [swapped[i], swapped[j]] = [swapped[j], swapped[i]];
        for (const h of heuristics) {
          for (const sr of splitRules) {
            const r = runPacking(swapped, stock, cut, sr, h);
            const s = packingScore(r);
            if (s < curScore) {
              curScore = s;
              curBest = r;
              order = swapped;
              improved = true;
            }
          }
        }
      }
    }
  }
  return { best: curBest, score: curScore };
}

// ─── Estrategias de ordenamiento (15) ────────────────────────────────────────
const SORTS: Array<(a: Part, b: Part) => number> = [
  (a, b) => b.width * b.height - a.width * a.height, // área desc
  (a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height), // lado largo desc
  (a, b) => b.width + b.height - (a.width + a.height), // perímetro desc
  (a, b) => b.height - a.height, // alto desc (para H)
  (a, b) => b.width - a.width, // ancho desc
  (a, b) => a.width * a.height - b.width * b.height, // área asc
  (a, b) => Math.min(b.width, b.height) - Math.min(a.width, a.height), // lado corto desc
  (a, b) => b.width / b.height - a.width / a.height, // ratio ancho/alto desc
  (a, b) => Math.max(a.width, a.height) - Math.max(b.width, b.height), // lado largo asc
  (a, b) => a.height - b.height, // alto asc
  (a, b) => a.width / a.height - b.width / b.height, // ratio asc
  (a, b) => Math.min(a.width, a.height) - Math.min(b.width, b.height), // lado corto asc
  (a, b) => a.width + a.height - (b.width + b.height), // perímetro asc
  (a, b) => a.width - b.width, // ancho asc
  (a, b) =>
    (b.width * b.height) / (b.width + b.height) -
    (a.width * a.height) / (a.width + a.height), // densidad desc
];

// Todas las heurísticas
const ALL_HEURISTICS: Heuristic[] = [BSSF, BLSF, BAF, BL, BR];

// Reglas de split — 'best' y 'mwf' son adaptativas, el resto son fijas
// Las adaptativas se evalúan siempre; las fijas se añaden en niveles más altos.
const SPLIT_HORIZ_BIAS: SplitRule[] = ["horiz", "best", "mwf", "sas"]; // prefieren H
const ALL_SPLITS: SplitRule[] = ["horiz", "best", "mwf", "sas", "vert", "las"];

// ─── Esfuerzo de cálculo (interno, antes "nivel de optimización") ─────────────
//
// El esfuerzo se modela en 5 escalones (1..5), igual que el antiguo selector de
// niveles que veía el usuario. Hoy NO se expone en la UI (feature #75): es un
// detalle interno que `chooseEffortLevel` selecciona según el tamaño del problema.
//
// Escalón | sortPasses | randPasses | heuristics       | splits              | swap
//    1    |     3      |     0      | [BSSF]           | horiz-bias (4)      | no
//    2    |     6      |    30      | [BSSF,BAF]       | horiz-bias (4)      | no
//    3    |    10      |   100      | [BSSF,BAF,BL]    | horiz-bias (4)      | no
//    4    |    15      |   300      | [BSSF,BAF,BL,BR] | all (6)             | sí
//    5    |    15      |   600      | all (5)           | all (6)             | sí (deep)

/** Escalón mínimo y máximo de esfuerzo (los índices de las tablas de arriba). */
const MIN_EFFORT = 1;
const MAX_EFFORT = 5;

/**
 * Umbrales (nº de instancias de pieza) que ACOTAN el esfuerzo a partir del cual se
 * baja de escalón. Con `n <= 25` se usa el esfuerzo máximo (escalón 5); cada umbral
 * superado baja un escalón hasta el mínimo (escalón 1).
 *
 * El porqué de acotar: el coste por pass crece con el nº de instancias, y la mejora
 * por intercambio de pares (`pairwiseImprove`, escalones 4-5) es ~O(n²) por iteración.
 * Con muchas piezas, el esfuerzo máximo (≈3 750 combos + swap profundo) puede congelar
 * el navegador durante segundos; bajar de escalón mantiene la fluidez sin que el
 * usuario tenga que elegir nada. Los cortes son los límites donde el escalón superior
 * dejaba de ser interactivo en pruebas manuales.
 */
const EFFORT_THRESHOLDS = [25, 60, 150, 400] as const;

/**
 * Elige, de forma DETERMINISTA, el escalón de esfuerzo (1..5) según el número total
 * de instancias de pieza a colocar (la suma de las cantidades ya expandida).
 *
 * Pocas piezas → esfuerzo máximo (5); a medida que crece el problema, baja de escalón
 * por cada umbral de `EFFORT_THRESHOLDS` superado, hasta el mínimo (1). Es invisible
 * para el usuario y reemplaza al antiguo `config.optimizationLevel`.
 */
export function chooseEffortLevel(totalInstances: number): number {
  const n = Math.max(0, Math.floor(totalInstances));
  let drops = 0;
  for (const threshold of EFFORT_THRESHOLDS) {
    if (n > threshold) drops++;
  }
  return Math.max(MIN_EFFORT, MAX_EFFORT - drops);
}

// ─── Optimizador principal ────────────────────────────────────────────────────
export function optimize(
  parts: Part[],
  stocks: Stock[],
  config: CutConfig,
): OptimizeResult | null {
  const cut = Number(config.cutThickness) || 0;
  const stock = stocks[0];
  if (!stock || parts.length === 0) return null;

  // Expandir por cantidad
  const expanded: ExpandedPart[] = [];
  parts.forEach((p) => {
    for (let i = 0; i < Math.max(1, Number(p.quantity) || 1); i++)
      expanded.push({ ...p, instanceId: `${p.id}_${i}` });
  });

  // Esfuerzo adaptativo e invisible: más combinaciones cuando hay pocas piezas,
  // acotado cuando hay muchas (ver `chooseEffortLevel`). Ya no viene de la config.
  const level = chooseEffortLevel(expanded.length);

  const sortPasses = [0, 3, 6, 10, 15, 15][level];
  const randPasses = [0, 0, 30, 100, 300, 600][level];
  const useSwap = level >= 4;

  const heuristics =
    level <= 1
      ? [BSSF]
      : level <= 2
        ? [BSSF, BAF]
        : level <= 3
          ? [BSSF, BAF, BL]
          : level <= 4
            ? [BSSF, BAF, BL, BR]
            : ALL_HEURISTICS;

  const splitRules = level <= 3 ? SPLIT_HORIZ_BIAS : ALL_SPLITS;

  let best: PackingResult | null = null,
    bestScore = Infinity;
  // Guardamos la mejor ordenación para el swap pass
  let bestOrder: Part[] | null = null;

  const tryResult = (sorted: Part[], hFn: Heuristic, sr: SplitRule): void => {
    const r = runPacking(sorted, stock, cut, sr, hFn);
    const s = packingScore(r);
    if (s < bestScore) {
      bestScore = s;
      best = r;
      bestOrder = sorted;
    }
  };

  // Passes deterministas: todas las combinaciones de sort × heuristic × split
  for (let si = 0; si < sortPasses; si++) {
    const sorted = [...expanded].sort(SORTS[si % SORTS.length]);
    for (const h of heuristics) for (const sr of splitRules) tryResult(sorted, h, sr);
  }

  // Passes aleatorios
  for (let ri = 0; ri < randPasses; ri++) {
    const shuffled = shuffle(expanded, ri * 2654435761 + 123456789);
    for (const h of heuristics) for (const sr of splitRules) tryResult(shuffled, h, sr);
  }

  // Mejora por intercambio de pares (solo niveles 4 y 5)
  if (useSwap && bestOrder && best) {
    const swapHeuristics = level >= 5 ? heuristics : [BSSF, BAF];
    const swapSplits: SplitRule[] = level >= 5 ? splitRules : ["horiz", "best", "sas"];
    const improved = pairwiseImprove(
      bestOrder,
      stock,
      cut,
      swapSplits,
      swapHeuristics,
      best,
      bestScore,
    );
    best = improved.best;
    bestScore = improved.score;
  }

  // `best` no es null aquí: hay al menos una pieza y un tablero (se hizo ≥1 pass).
  const resolved = best as PackingResult;

  // Estadísticas globales
  const totalArea = resolved.sheets.reduce((s, sh) => s + sh.totalArea, 0);
  const usedArea = resolved.sheets.reduce((s, sh) => s + sh.usedArea, 0);
  const placed = resolved.sheets.reduce((s, sh) => s + sh.placements.length, 0);

  // Conteo de combinaciones evaluadas (aprox)
  const combos =
    sortPasses * heuristics.length * splitRules.length +
    randPasses * heuristics.length * splitRules.length;

  return {
    ...resolved,
    stats: {
      totalSheets: resolved.sheets.length,
      totalParts: expanded.length,
      placedParts: placed,
      unplacedParts: resolved.unplaced.length,
      totalSheetArea: totalArea,
      totalUsedArea: usedArea,
      totalWasteArea: totalArea - usedArea,
      wastePercent: totalArea > 0 ? Math.round((1 - usedArea / totalArea) * 100) : 0,
      combosEvaluated: combos,
    },
  };
}
